// The "anatomy" view: the sound (waveform or spectrogram) on top, the file's
// bytes (a nested byte map) at the bottom, and ribbons that link each block of
// bytes to the moment it plays.
import { store, type State, type Target } from '../state';
import { kids, hasKids, timeOf, type Model } from '../model';
import type { Segment, SegmentLink } from '../parsers/types';
import { Canvas, h, hideTip, richText, showTip, tip } from './dom';
import { alpha, onTheme, theme } from './theme';
import { fmtBytes, plural } from '../parsers/bits';
import { KIND_LABEL } from '../content/kinds';
import SpectroWorker from '../audio/spectrogram.worker.ts?worker';
import { LUT } from '../audio/colormap';
import { LANE_COLORS } from './codec';

const RIBBON_H = 84;
const ROWS = [26, 16, 12]; // byte map row heights by depth
const BYTE_AXIS = 18;
const BM_H = ROWS.reduce((a, b) => a + b, 0) + BYTE_AXIS + 4;
const AXIS_H = 20;
const SCROLL_H = 10;
const RATE_H = 34;
const LANE_ROW = 13; // height of one codec decision lane

export function fmtTime(t: number, span = 10): string {
  const sign = t < 0 ? '−' : '';
  t = Math.abs(t);
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  const dec = span < 0.05 ? 4 : span < 1 ? 3 : span < 20 ? 2 : span < 120 ? 1 : 0;
  const ss = s.toFixed(dec).padStart(dec ? dec + 3 : 2, '0');
  return `${sign}${m}:${ss}`;
}
const fmtOff = (b: number) => `0x${Math.floor(b).toString(16).toUpperCase()}`;

/** Which segment a Target points at, if any. */
const segOf = (t: Target | null) => (t && t.kind === 'seg' ? t.seg : null);

export class Anatomy {
  readonly el: HTMLElement;
  private tl: Canvas; // waveform / spectrogram base layer
  private tlo: Canvas; // timeline overlay (hover, selection, playhead)
  private rb: Canvas; // ribbon
  private bm: Canvas; // byte map base
  private bmo: Canvas; // byte map overlay
  private status: HTMLElement;
  private readout!: HTMLElement;
  private playBtn!: HTMLButtonElement;
  private laneBtns: HTMLButtonElement[] = [];
  private decodeMsg: HTMLElement;
  private lanes: { y: number; h: number; ch: number }[] = [];
  private waveTop = 0;
  private waveH = 0;
  private rateY = 0;
  private axisY = 0;
  private scrollY = 0;
  private width = 0;
  private raf = 0;
  private dirty = { base: true, over: true, bytes: true };
  private spectro: Spectro | null = null;
  private maxRate = 0;
  private codecY = 0;
  private codecLaneLabels: HTMLElement[] = [];
  private linkZoom = false;
  private linkBtn!: HTMLButtonElement;
  private ro: ResizeObserver;

  constructor(private m: Model) {
    const chans = store.s.peaks ? store.s.peaks.channels.length : m.pf.audio.channels ?? 2;
    const shown = Math.max(1, Math.min(chans, 8));
    const laneH = shown <= 2 ? 78 : Math.max(26, Math.floor(180 / shown));
    const nCodec = store.s.codec?.c.lanes.length ?? 0;
    const codecH = nCodec ? nCodec * LANE_ROW + 6 : 0;
    const tlH = laneH * shown + RATE_H + codecH + AXIS_H + SCROLL_H + 6;
    this.tl = new Canvas('layer', tlH);
    this.tlo = new Canvas('layer cv-timeline', tlH);
    this.rb = new Canvas('cv-ribbon', RIBBON_H);
    this.bm = new Canvas('layer', BM_H);
    this.bmo = new Canvas('layer cv-bytes', BM_H);
    let y = 4;
    for (let c = 0; c < shown; c++) {
      this.lanes.push({ y, h: laneH, ch: c });
      y += laneH;
    }
    this.waveTop = 4;
    this.waveH = y - 4;
    this.rateY = y + 2;
    this.codecY = this.rateY + RATE_H + 2;
    this.axisY = this.rateY + RATE_H + codecH;
    this.scrollY = this.axisY + AXIS_H;

    // Peak bitrate for the bitrate lane scale (ignore the top 0.5% as outliers).
    const rates = m.frames.map((f) => ((f.end - f.start) * 8) / Math.max(1e-6, f.time![1] - f.time![0])).sort((a, b) => a - b);
    this.maxRate = rates.length ? rates[Math.min(rates.length - 1, Math.floor(rates.length * 0.995))] * 1.1 : 1;

    this.decodeMsg = h('div', { class: 'decode-msg', hidden: true });
    this.status = h('div', { class: 'status-line' }, h('span', { class: 'dim' }, 'Hover the sound or the byte map to see how they connect.'));
    const tlStage = h('div', { class: 'stage', style: { height: tlH + 'px' } }, this.tl.el, this.tlo.el);
    const rbStage = h('div', { class: 'stage' }, this.rb.el);
    const bmStage = h('div', { class: 'stage', style: { height: BM_H + 'px' } }, this.bm.el, this.bmo.el);
    const lab = (text: string, top: number, title: string) => {
      const l = h('span', { class: 'lane-label', style: { top: top + 'px' } }, text);
      l.title = title;
      return l;
    };
    tlStage.append(lab('Sound', 6, 'What you hear: the decoded audio.'), lab('Bitrate', this.rateY + 2, 'How many bits each moment of sound uses.'));
    store.s.codec?.c.lanes.forEach((ln, k) => {
      const l = lab(ln.name, this.codecY + k * LANE_ROW - 1, '');
      l.classList.add('codec-lane-label');
      this.codecLaneLabels.push(l);
      tlStage.append(l);
    });
    store.s.codec?.on(() => this.invalidate('base'));
    if (store.s.lane === 'stored') store.s.codec?.requestColumns();
    bmStage.append(lab('File bytes', ROWS[0] + ROWS[1] + ROWS[2] + 2, 'Every byte of the file, left to right.'));
    const rbLab = lab('Which bytes make which sound', RIBBON_H / 2 - 8, '');
    rbLab.style.left = 'auto';
    rbLab.style.right = '8px';
    rbStage.append(rbLab);

    this.el = h('section', { class: 'card anatomy' }, this.toolbar(), this.decodeMsg, tlStage, rbStage, bmStage, this.status);

    this.bindTimeline();
    this.bindBytes();
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(tlStage);
    onTheme(() => this.invalidate('base', 'bytes', 'over'));
    store.sub((s, p) => this.onState(s, p));
    this.onState(store.s, { ...store.s, decode: 'idle' as const, peaks: null });
  }

  private toolbar(): HTMLElement {
    this.playBtn = h('button', { class: 'icon-btn play', title: 'Play / pause (Space)', 'aria-label': 'Play' }, '▶');
    this.playBtn.onclick = () => togglePlay();
    this.readout = h('span', { class: 'time-readout' }, '0:00.00');
    const mk = (label: string, lane: State['lane'], title: string) => {
      const b = h('button', { class: 'seg-btn', 'aria-pressed': String(store.s.lane === lane), title, 'data-lane': lane }, label);
      b.onclick = () => store.set({ lane });
      this.laneBtns.push(b);
      return b;
    };
    const loop = h('button', { class: 'icon-btn', title: 'Loop the selection (L)', 'aria-pressed': String(store.s.loop) }, '⟲');
    loop.onclick = () => store.set({ loop: !store.s.loop });
    store.sub((s, p) => { if (s.loop !== p.loop) loop.setAttribute('aria-pressed', String(s.loop)); });
    this.linkBtn = h('button', { class: 'seg-btn', 'aria-pressed': 'false', title: 'When on, zooming the sound also zooms the byte map to the same bytes.' }, 'Link zoom');
    this.linkBtn.onclick = () => {
      this.linkZoom = !this.linkZoom;
      this.linkBtn.setAttribute('aria-pressed', String(this.linkZoom));
      if (this.linkZoom) this.syncBytesToTime();
    };
    const zb = (label: string, title: string, fn: () => void) => {
      const b = h('button', { class: 'icon-btn', title, 'aria-label': title }, label);
      b.onclick = fn;
      return b;
    };
    const hint = h('span', { class: 'hint' }, '⌘/Ctrl + scroll to zoom · Shift + scroll to pan');
    return h(
      'div',
      { class: 'toolbar' },
      this.playBtn,
      loop,
      this.readout,
      h('div', { class: 'seg' }, mk('Waveform', 'wave', 'Show amplitude over time (S)'), mk('Spectrogram', 'spec', 'Show the frequencies you hear (S)'),
        store.s.codec?.hasColumns ? mk('Stored', 'stored', 'Show the frequency coefficients the file actually stores — before the decoder turns them back into sound (S)') : null),
      h('div', { class: 'spacer' }),
      hint,
      h('div', { class: 'seg' }, this.linkBtn),
      zb('−', 'Zoom out (−)', () => zoomTime(2)),
      zb('+', 'Zoom in (+)', () => zoomTime(0.5)),
      zb('⤢', 'Zoom to selection (Z)', () => zoomToSelection()),
      zb('▭', 'Show everything (F)', () => fitAll()),
    );
  }

  destroy() {
    this.ro.disconnect();
    cancelAnimationFrame(this.raf);
    this.spectro?.dispose();
  }

  // ---------------------------------------------------------------- geometry
  private tx(t: number) {
    const [a, b] = store.s.view;
    return ((t - a) / (b - a)) * this.width;
  }
  private xt(x: number) {
    const [a, b] = store.s.view;
    return a + (x / this.width) * (b - a);
  }
  private bx(byte: number) {
    const [a, b] = store.s.bview;
    return ((this.m.layout.toU(byte) - a) / (b - a)) * this.width;
  }
  private xb(x: number) {
    const [a, b] = store.s.bview;
    return this.m.layout.toByte(a + (x / this.width) * (b - a));
  }
  private rowY(depth: number) {
    let y = 0;
    for (let i = 0; i < depth; i++) y += ROWS[i];
    return y;
  }

  // ---------------------------------------------------------------- state
  private onState(s: State, p: State) {
    if (s.peaks !== p.peaks || s.decode !== p.decode) {
      this.invalidate('base');
      this.updateDecodeMsg();
      if (s.peaks && !this.spectro) this.spectro = new Spectro(s.peaks, () => this.invalidate('base'));
    }
    if (s.view !== p.view || s.lane !== p.lane) {
      this.invalidate('base', 'over');
      if (this.linkZoom && s.view !== p.view) this.syncBytesToTime();
    }
    if (s.lane !== p.lane) {
      this.laneBtns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.lane === s.lane)));
      if (s.lane === 'stored') s.codec?.requestColumns();
    }
    if (s.bview !== p.bview) this.invalidate('bytes', 'over');
    if (s.hover !== p.hover || s.sel !== p.sel) this.invalidate('over', 'bytes');
    if (s.player !== p.player && s.player) {
      s.player.on(() => {
        this.playBtn.textContent = s.player!.playing ? '❚❚' : '▶';
        this.invalidate('over');
      });
    }
  }

  private updateDecodeMsg() {
    const s = store.s;
    const el = this.decodeMsg;
    if (s.decode === 'decoding') {
      el.hidden = false;
      el.className = 'decode-msg';
      el.textContent = 'Decoding audio…';
    } else if (s.decode === 'error') {
      el.hidden = false;
      el.className = 'decode-msg warn';
      el.textContent = `This browser cannot decode this audio (${s.decodeError ?? 'unknown error'}), so there is no waveform or playback. The file structure below is still complete.`;
    } else if (s.previewOnly) {
      el.hidden = false;
      el.className = 'decode-msg';
      el.textContent = 'This is a long file, so the waveform uses a low-resolution preview. Playback is full quality.';
    } else el.hidden = true;
  }

  private syncBytesToTime() {
    const [t0, t1] = store.s.view;
    const r = this.m.bytesForTime(t0, t1);
    if (!r) return;
    const u0 = this.m.layout.toU(r[0]), u1 = this.m.layout.toU(r[1]);
    if (u1 - u0 > 1e-9) store.set({ bview: [u0, u1] });
  }

  invalidate(...what: ('base' | 'over' | 'bytes')[]) {
    for (const w of what) this.dirty[w] = true;
    if (!this.raf) this.raf = requestAnimationFrame(() => this.draw());
  }

  private resize() {
    const w = Math.floor(this.el.clientWidth - 2);
    if (w <= 0) return;
    this.width = w;
    const ch = [this.tl.fit(w), this.tlo.fit(w), this.rb.fit(w), this.bm.fit(w), this.bmo.fit(w)];
    if (ch.some(Boolean)) this.invalidate('base', 'over', 'bytes');
  }

  private draw() {
    this.raf = 0;
    if (!this.width) return;
    if (this.dirty.base) this.drawBase();
    if (this.dirty.bytes) this.drawBytes();
    this.drawOverlay();
    this.dirty = { base: false, over: false, bytes: false };
    const pl = store.s.player;
    if (pl?.playing) {
      this.followPlayhead(pl.time);
      this.invalidate('over');
    }
    this.readout.textContent = `${fmtTime(pl?.time ?? 0, 1)} / ${fmtTime(this.m.duration, 1)}`;
  }

  private followPlayhead(t: number) {
    const [a, b] = store.s.view;
    if (t > b || t < a) {
      const w = b - a;
      const na = Math.max(0, Math.min(this.m.duration - w, t - w * 0.05));
      store.set({ view: [na, na + w] });
    }
  }

  // ---------------------------------------------------------------- base timeline
  private drawBase() {
    const c = this.tl.ctx, T = theme(), W = this.width;
    this.tl.clear();
    const s = store.s;
    const [v0, v1] = s.view;
    // Frame bands: when frames are wide enough, shade alternate frames so you can see them.
    const i0 = Math.max(0, this.m.frameIndexAtTime(v0));
    const i1 = Math.max(0, this.m.frameIndexAtTime(v1));
    const nVisible = i1 - i0 + 1;
    const showFrames = this.m.frames.length > 1 && nVisible < W / 6;
    if (showFrames) {
      for (let i = i0; i <= i1; i++) {
        const f = this.m.frames[i];
        const x0 = this.tx(f.time![0]), x1 = this.tx(f.time![1]);
        if (i % 2) {
          c.fillStyle = alpha(T.text, T.dark ? 0.035 : 0.04);
          c.fillRect(x0, this.waveTop, x1 - x0, this.waveH + RATE_H);
        }
        c.fillStyle = alpha(T.text, 0.1);
        c.fillRect(Math.round(x0), this.waveTop, 1, this.waveH + RATE_H);
      }
    }

    if (s.lane === 'stored' && s.codec?.hasColumns) {
      this.drawStored(c, T, W);
    } else if (s.lane === 'spec' && this.spectro) {
      this.spectro.draw(c, 0, this.waveTop, W, this.waveH, s.view);
      // Frequency labels.
      c.font = '10px ' + getComputedStyle(document.body).getPropertyValue('--mono');
      c.fillStyle = 'rgba(255,255,255,0.75)';
      const nyq = this.spectro.maxF;
      for (const khz of [2, 5, 10, 15, 20, 30, 40]) {
        if (khz * 1000 >= nyq) continue;
        const y = this.waveTop + this.waveH * (1 - (khz * 1000) / nyq);
        c.fillRect(W - 6, y, 6, 1);
        c.fillText(`${khz}k`, W - 30, y + 3);
      }
    } else if (s.peaks) {
      this.drawWave(c, T);
    } else {
      c.fillStyle = T.muted;
      c.font = '13px Inter, sans-serif';
      c.textAlign = 'center';
      c.fillText(s.decode === 'decoding' ? 'Decoding audio…' : s.decode === 'error' ? 'No waveform: the browser cannot decode this audio.' : '', W / 2, this.waveTop + this.waveH / 2);
      c.textAlign = 'left';
    }

    this.drawRate(c, T, i0, i1);
    this.drawCodecLanes(c, T, i0, i1);
    this.drawAxis(c, T);
  }

  private drawWave(c: CanvasRenderingContext2D, T: ReturnType<typeof theme>) {
    const pk = store.s.peaks!;
    const [v0, v1] = store.s.view;
    const W = this.width;
    const sr = pk.sampleRate;
    const spp = ((v1 - v0) * sr) / W; // samples per pixel
    for (const lane of this.lanes) {
      const ch = Math.min(lane.ch, pk.channels.length - 1);
      const mid = lane.y + lane.h / 2;
      const amp = lane.h / 2 - 3;
      c.fillStyle = alpha(T.text, 0.08);
      c.fillRect(0, Math.round(mid), W, 1);
      if (lane.ch > 0) {
        c.fillStyle = T.line;
        c.fillRect(0, lane.y, W, 1);
      }
      if (spp > 1.5) {
        c.fillStyle = T.wave;
        for (let x = 0; x < W; x++) {
          const s0 = (v0 + (x / W) * (v1 - v0)) * sr;
          const [lo, hi] = pk.range(ch, s0, s0 + spp);
          const y0 = mid - hi * amp, y1 = mid - lo * amp;
          c.fillRect(x, y0, 1, Math.max(1, y1 - y0));
        }
      } else {
        // Zoomed in far enough to see individual samples.
        const d = pk.channels[ch];
        const s0 = Math.max(0, Math.floor(v0 * sr) - 1), s1 = Math.min(d.length - 1, Math.ceil(v1 * sr) + 1);
        c.strokeStyle = T.wave;
        c.lineWidth = 1.5;
        c.beginPath();
        for (let i = s0; i <= s1; i++) {
          const x = this.tx(i / sr), y = mid - d[i] * amp;
          if (i === s0) c.moveTo(x, y);
          else c.lineTo(x, y);
        }
        c.stroke();
        if (spp < 0.12) {
          c.fillStyle = T.wave;
          for (let i = s0; i <= s1; i++) {
            c.beginPath();
            c.arc(this.tx(i / sr), mid - d[i] * amp, 2.2, 0, Math.PI * 2);
            c.fill();
          }
        }
      }
      if (this.lanes.length > 1) {
        c.fillStyle = T.muted;
        c.font = '10px Inter, sans-serif';
        const names = this.lanes.length === 2 ? ['L', 'R'] : [];
        c.fillText(names[lane.ch] ?? `Ch ${lane.ch + 1}`, W - 18, lane.y + 12);
      }
    }
  }

  /** Codec decision lanes: one colored strip per decision (window type, stereo mode, predictor…). */
  private drawCodecLanes(c: CanvasRenderingContext2D, T: ReturnType<typeof theme>, i0: number, i1: number) {
    const cc = store.s.codec;
    if (!cc) return;
    const F = this.m.frames;
    c.fillStyle = T.line;
    c.fillRect(0, this.codecY - 3, this.width, 1);
    cc.lanes.forEach((vals, k) => {
      const y = this.codecY + k * LANE_ROW, hh = LANE_ROW - 3;
      let i = i0;
      while (i <= i1) {
        const v = vals[i];
        let j = i;
        while (j < i1 && vals[j + 1] === v && F[j + 1].time![0] - F[j].time![1] < 1e-6) j++;
        const x0 = this.tx(F[i].time![0]), x1 = this.tx(F[j].time![1]);
        c.fillStyle = v < 0 ? alpha(T.text, i >= cc.decided ? 0.06 : 0.03) : LANE_COLORS[v % LANE_COLORS.length];
        c.fillRect(x0, y, Math.max(0.8, x1 - x0), hh);
        if (x1 - x0 > 5 && v >= 0) {
          c.fillStyle = alpha(T.bg.startsWith('#') ? T.bg : '#000000', 0.5);
          c.fillRect(Math.round(x1) - 0.5, y, 1, hh);
        }
        i = j + 1;
      }
    });
  }

  /** The "stored" spectrogram: coefficients decoded straight from the file, before synthesis. */
  private storedCanvas: HTMLCanvasElement | null = null;
  private drawStored(c: CanvasRenderingContext2D, T: ReturnType<typeof theme>, W: number) {
    const cc = store.s.codec!;
    const y0 = this.waveTop, H = Math.round(this.waveH);
    const [v0, v1] = store.s.view;
    const off = (this.storedCanvas ??= document.createElement('canvas'));
    if (off.width !== W || off.height !== H) { off.width = W; off.height = H; }
    const ctx = off.getContext('2d')!;
    const img = ctx.createImageData(W, H);
    const px = img.data;
    const nyq = cc.maxNyquist || (this.m.pf.audio.sampleRate ?? 44100) / 2;
    for (let x = 0; x < W; x++) {
      const t = v0 + ((x + 0.5) / W) * (v1 - v0);
      const ci = cc.columnAt(t);
      const col = ci >= 0 ? cc.columns[ci] : null;
      for (let y = 0; y < H; y++) {
        const o = (y * W + x) * 4;
        px[o + 3] = 255;
        if (!col) { px[o] = 14; px[o + 1] = 17; px[o + 2] = 24; continue; }
        const fTop = nyq * (1 - y / H), fBot = nyq * (1 - (y + 1) / H);
        if (fBot >= col.nyquist) { px[o] = 14; px[o + 1] = 17; px[o + 2] = 24; continue; }
        const n = col.v.length;
        const a = Math.floor((fBot / col.nyquist) * n), b = Math.min(n, Math.max(a + 1, Math.ceil((fTop / col.nyquist) * n)));
        let mx = 0;
        for (let k = a; k < b; k++) if (col.v[k] > mx) mx = col.v[k];
        if (mx === 0) {
          // Exactly zero: the encoder stored nothing here. Shown in slate, not black, so it stands out.
          px[o] = 30; px[o + 1] = 38; px[o + 2] = 58;
        } else {
          px[o] = LUT[mx * 3]; px[o + 1] = LUT[mx * 3 + 1]; px[o + 2] = LUT[mx * 3 + 2];
        }
      }
    }
    ctx.putImageData(img, 0, 0);
    c.drawImage(off, 0, y0, W, this.waveH);
    c.font = '10px "JetBrains Mono", monospace';
    c.fillStyle = 'rgba(255,255,255,0.75)';
    for (const khz of [2, 5, 10, 15, 20]) {
      if (khz * 1000 >= nyq) continue;
      const y = y0 + this.waveH * (1 - (khz * 1000) / nyq);
      c.fillRect(W - 6, y, 6, 1);
      c.fillText(`${khz}k`, W - 30, y + 3);
    }
    if (cc.columnsDone < this.m.frames.length) {
      const pct = Math.round((cc.columnsDone / Math.max(1, this.m.frames.length)) * 100);
      c.fillStyle = 'rgba(0,0,0,0.6)';
      c.fillRect(W / 2 - 110, y0 + 8, 220, 20);
      c.fillStyle = '#fff';
      c.textAlign = 'center';
      c.font = '12px Inter, sans-serif';
      c.fillText(`Decoding stored coefficients… ${pct}%`, W / 2, y0 + 22);
      c.textAlign = 'left';
    }
    void T;
  }

  /** Bitrate lane: bits per second of the frames under each pixel column. */
  private drawRate(c: CanvasRenderingContext2D, T: ReturnType<typeof theme>, i0: number, i1: number) {
    const W = this.width, y = this.rateY, H = RATE_H - 4;
    c.fillStyle = T.line;
    c.fillRect(0, y - 2, W, 1);
    if (!this.m.frames.length) return;
    const bits = new Float64Array(W), secs = new Float64Array(W);
    for (let i = i0; i <= i1; i++) {
      const f = this.m.frames[i];
      const d = f.time![1] - f.time![0];
      if (d <= 0) continue;
      const rate = ((f.end - f.start) * 8) / d;
      const xa = this.tx(f.time![0]), xb = this.tx(f.time![1]);
      const ca = Math.max(0, Math.floor(xa)), cb = Math.min(W - 1, Math.floor(xb));
      if (cb > ca) {
        for (let x = ca; x <= cb; x++) { bits[x] += rate; secs[x] += 1; }
      } else if (ca >= 0 && ca < W) {
        bits[ca] += (f.end - f.start) * 8;
        secs[ca] += d;
      }
    }
    c.fillStyle = alpha(T.kind.audio, 0.55);
    let lastVal = 0;
    for (let x = 0; x < W; x++) {
      if (secs[x] > 0) lastVal = bits[x] / secs[x];
      else continue;
      const hh = Math.min(1, lastVal / this.maxRate) * H;
      c.fillRect(x, y + H - hh, 1, hh);
    }
    c.fillStyle = T.muted;
    c.font = '10px Inter, sans-serif';
    c.textAlign = 'right';
    c.fillText(`${Math.round(this.maxRate / 1000)} kbps`, W - 6, y + 9);
    c.textAlign = 'left';
  }

  private drawAxis(c: CanvasRenderingContext2D, T: ReturnType<typeof theme>) {
    const W = this.width, y = this.axisY;
    const [v0, v1] = store.s.view;
    const span = v1 - v0;
    c.fillStyle = T.line;
    c.fillRect(0, y, W, 1);
    const steps = [0.0001, 0.0002, 0.0005, 0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 1200, 1800, 3600];
    const step = steps.find((st) => (st / span) * W >= 90) ?? 3600;
    c.font = '10.5px "JetBrains Mono", monospace';
    c.fillStyle = T.muted;
    for (let t = Math.ceil(v0 / step) * step; t <= v1; t += step) {
      const x = this.tx(t);
      c.fillRect(Math.round(x), y, 1, 5);
      c.fillText(fmtTime(t, step * 10), x + 3, y + 14);
    }
    // Scrollbar showing the visible window within the whole duration.
    const D = this.m.duration || 1;
    const sy = this.scrollY + 2;
    c.fillStyle = alpha(T.text, 0.06);
    c.fillRect(0, sy, W, SCROLL_H - 4);
    c.fillStyle = alpha(T.text, span < D * 0.999 ? 0.3 : 0.12);
    const a = (v0 / D) * W, b = (v1 / D) * W;
    c.fillRect(a, sy, Math.max(4, b - a), SCROLL_H - 4);
  }

  // ---------------------------------------------------------------- byte map
  private drawBytes() {
    const c = this.bm.ctx, T = theme(), W = this.width;
    this.bm.clear();
    const [u0, u1] = store.s.bview;
    const bLo = this.m.layout.toByte(u0), bHi = this.m.layout.toByte(u1);
    c.font = '11px Inter, sans-serif';
    const drawList = (list: Segment[], depth: number) => {
      if (depth >= ROWS.length) return;
      const y = this.rowY(depth) + 1, hh = ROWS[depth] - 2;
      let i = firstEndingAfter(list, bLo);
      let alt = i;
      for (; i < list.length && list[i].start < bHi; i++, alt++) {
        const s = list[i];
        const x0 = this.bx(s.start), x1 = this.bx(s.end);
        const w = x1 - x0;
        if (w < 0.05) continue;
        const col = T.kind[s.kind];
        c.fillStyle = alt % 2 && depth > 0 ? alpha(col, 0.72) : col;
        c.fillRect(x0, y, Math.max(w, 0.6), hh);
        if (w >= 5) {
          c.fillStyle = T.panel;
          c.fillRect(Math.floor(x1) - 1, y, 1, hh);
        }
        if (depth === 0 && this.m.layout.inflated.has(s) && w > 3) hatch(c, x0, y, w, hh, T.panel);
        if (s.warning && w > 8) {
          c.fillStyle = T.warn;
          c.fillRect(x0, y, w, 2);
        }
        if (w > 40 && ROWS[depth] >= 14) {
          const lx = Math.max(x0, 0) + 5;
          const room = Math.min(x1, W) - lx - 4;
          if (room > 20) {
            c.save();
            c.beginPath();
            c.rect(lx, y, room, hh);
            c.clip();
            c.fillStyle = 'rgba(8,10,16,0.85)';
            c.font = depth === 0 ? '600 11px Inter, sans-serif' : '10px Inter, sans-serif';
            c.fillText(s.name, lx, y + hh / 2 + 4);
            c.restore();
          }
        }
        // Descend when children would be visible.
        if (depth + 1 < ROWS.length && hasKids(s)) {
          const lazy = !Array.isArray(s.children);
          if (!lazy || w > 60) {
            const ch = kids(s);
            if (ch.length && w / ch.length > 0.02) drawList(ch, depth + 1);
          }
        }
      }
    };
    drawList(this.m.top, 0);

    // Byte axis.
    const y = this.rowY(ROWS.length) + 2;
    c.fillStyle = T.line;
    c.fillRect(0, y, W, 1);
    c.font = '10.5px "JetBrains Mono", monospace';
    c.fillStyle = T.muted;
    const n = Math.max(2, Math.floor(W / 120));
    const spanBytes = this.xb(W) - this.xb(0);
    for (let k = 0; k <= n; k++) {
      const x = (k / n) * W;
      const b = this.xb(x);
      c.fillRect(Math.min(W - 1, Math.round(x)), y, 1, 4);
      const lab = spanBytes < 50000 ? Math.round(b).toLocaleString('en-US') : fmtBytes(Math.round(b));
      const tw = c.measureText(lab).width;
      c.fillText(lab, Math.max(2, Math.min(W - tw - 2, x - tw / 2)), y + 14);
    }
  }

  // ---------------------------------------------------------------- overlay
  private drawOverlay() {
    const T = theme(), W = this.width;
    const c = this.tlo.ctx, r = this.rb.ctx, b = this.bmo.ctx;
    this.tlo.clear();
    this.rb.clear();
    this.bmo.clear();
    const s = store.s;

    this.drawRibbon(r, T);

    // Selection first, then hover on top.
    for (const [tgt, strong] of [[s.sel, true], [s.hover, false]] as const) {
      if (!tgt) continue;
      const seg = segOf(tgt);
      let tr: [number, number] | null = null;
      let br: [number, number] | null = null;
      let col = T.sel;
      if (seg) {
        tr = timeOf(this.m, seg);
        br = [seg.start, seg.end];
        col = strong ? T.sel : T.kind[seg.kind];
      } else if (tgt.kind === 'time') {
        tr = [tgt.t0, tgt.t1];
        br = this.m.bytesForTime(tgt.t0, tgt.t1);
      } else if (tgt.kind === 'probe') {
        const f = tgt.t !== null ? this.frameAtTime(tgt.t) : tgt.byte !== null ? this.frameAtByte(tgt.byte) : null;
        if (f && (this.tx(f.time![1]) - this.tx(f.time![0]) >= 3 || this.bx(f.end) - this.bx(f.start) >= 3)) {
          tr = f.time!;
          br = [f.start, f.end];
          col = T.kind.audio;
        }
        this.drawProbe(c, r, b, T, tgt);
      }
      if (tr) this.bandTime(c, tr, col, strong);
      if (tr && br) this.bandRibbon(r, tr, br, col, strong);
      if (br) this.bandBytes(b, br, seg, col, strong);
    }

    const selSeg = segOf(s.sel);
    if (selSeg) this.drawLinks(r, b, T, selSeg);

    // Playhead through all three layers: watch the read position move through the file.
    const pl = s.player;
    if (pl && (pl.playing || pl.time > 0)) {
      const t = pl.time;
      const x = this.tx(t);
      c.fillStyle = T.playhead;
      c.fillRect(Math.round(x), 0, 1.5, this.axisY);
      const byte = this.m.byteAtTime(t);
      if (byte !== null) {
        const bx = this.bx(byte);
        r.strokeStyle = alpha(T.playhead, 0.9);
        r.lineWidth = 1.5;
        r.beginPath();
        r.moveTo(x, 0);
        r.bezierCurveTo(x, RIBBON_H / 2, bx, RIBBON_H / 2, bx, RIBBON_H);
        r.stroke();
        b.fillStyle = T.playhead;
        b.fillRect(Math.round(bx), 0, 1.5, this.rowY(ROWS.length));
        b.beginPath();
        b.moveTo(bx - 5, 0);
        b.lineTo(bx + 5, 0);
        b.lineTo(bx, 6);
        b.fill();
      }
    }
    void W;
  }

  /** Arcs from the selected block to the other bytes it depends on (bit reservoir, sample tables…). */
  private drawLinks(r: CanvasRenderingContext2D, b: CanvasRenderingContext2D, T: ReturnType<typeof theme>, seg: Segment) {
    const links = linksOf(seg);
    if (!links.length) return;
    const H = RIBBON_H;
    const sx = (this.bx(seg.start) + this.bx(seg.end)) / 2;
    const col = T.warn;
    r.font = '600 10.5px Inter, sans-serif';
    links.forEach((l, i) => {
      const x0 = this.bx(l.start), x1 = Math.max(x0 + 2, this.bx(l.end));
      const lx = (x0 + x1) / 2;
      const peak = 18 + i * 9;
      r.strokeStyle = col;
      r.lineWidth = 1.5;
      r.beginPath();
      r.moveTo(sx, H);
      r.bezierCurveTo(sx, peak, lx, peak, lx, H);
      r.stroke();
      // Arrow head at the linked end.
      r.fillStyle = col;
      r.beginPath();
      r.moveTo(lx - 4, H - 7);
      r.lineTo(lx + 4, H - 7);
      r.lineTo(lx, H);
      r.fill();
      // Labels stack in a column beside the targets, so close targets stay readable.
      const tw = r.measureText(l.label).width;
      const right = lx < sx;
      let tx = right ? lx + 8 : lx - tw - 8;
      tx = Math.max(4, Math.min(this.width - tw - 8, tx));
      const ty = 12 + i * 14;
      r.fillStyle = alpha(T.panel.startsWith('#') ? T.panel : '#121722', 0.92);
      r.fillRect(tx - 4, ty - 10, tw + 8, 14);
      r.fillStyle = col;
      r.fillText(l.label, tx, ty);
      b.strokeStyle = col;
      b.setLineDash([3, 2]);
      b.lineWidth = 1.5;
      b.strokeRect(x0, 1, x1 - x0, this.rowY(ROWS.length) - 2);
      b.setLineDash([]);
    });
  }

  private frameAtTime(t: number): Segment | null {
    const i = this.m.frameIndexAtTime(t);
    const f = i >= 0 ? this.m.frames[i] : null;
    return f && t < f.time![1] ? f : null;
  }
  private frameAtByte(b: number): Segment | null {
    const i = this.m.frameIndexAtByte(b);
    return i >= 0 ? this.m.frames[i] : null;
  }

  /** The ribbon: one band per run of frames, from their time (top) to their bytes (bottom). */
  private drawRibbon(r: CanvasRenderingContext2D, T: ReturnType<typeof theme>) {
    const F = this.m.frames;
    if (!F.length) {
      r.fillStyle = T.muted;
      r.font = '12px Inter, sans-serif';
      r.textAlign = 'center';
      r.fillText('This file has no timed audio blocks to link.', this.width / 2, RIBBON_H / 2 + 4);
      r.textAlign = 'left';
      return;
    }
    const [v0, v1] = store.s.view;
    const i0 = Math.max(0, this.m.frameIndexAtTime(v0));
    const i1 = Math.max(0, this.m.frameIndexAtTime(v1));
    const H = RIBBON_H, mid = H / 2;
    const col = T.kind.audio;
    let run = 0;
    let i = i0;
    while (i <= i1) {
      const a = F[i];
      let j = i;
      // Merge neighbours until the band is at least ~2px wide on both ends.
      while (j < i1 && (this.tx(F[j].time![1]) - this.tx(a.time![0]) < 2 || this.bx(F[j].end) - this.bx(a.start) < 2)) j++;
      const z = F[j];
      const tx0 = this.tx(a.time![0]), tx1 = this.tx(z.time![1]);
      const bx0 = this.bx(a.start), bx1 = this.bx(z.end);
      r.fillStyle = alpha(col, run % 2 ? 0.2 : 0.3);
      r.beginPath();
      r.moveTo(tx0, 0);
      r.bezierCurveTo(tx0, mid, bx0, mid, bx0, H);
      r.lineTo(bx1, H);
      r.bezierCurveTo(bx1, mid, tx1, mid, tx1, 0);
      r.closePath();
      r.fill();
      run++;
      i = j + 1;
    }
  }

  private bandTime(c: CanvasRenderingContext2D, tr: [number, number], col: string, strong: boolean) {
    const x0 = this.tx(tr[0]), x1 = this.tx(tr[1]);
    const w = Math.max(1.5, x1 - x0);
    c.fillStyle = alpha(col, strong ? 0.16 : 0.12);
    c.fillRect(x0, 0, w, this.axisY);
    c.fillStyle = alpha(col, strong ? 0.95 : 0.6);
    c.fillRect(x0, 0, 1.5, this.axisY);
    c.fillRect(x0 + w - 1.5, 0, 1.5, this.axisY);
    // Mark on the scrollbar, so an off-screen selection is still findable.
    const D = this.m.duration || 1;
    c.fillRect((tr[0] / D) * this.width, this.scrollY + 1, Math.max(2, ((tr[1] - tr[0]) / D) * this.width), SCROLL_H - 2);
  }

  private bandRibbon(r: CanvasRenderingContext2D, tr: [number, number], br: [number, number], col: string, strong: boolean) {
    const H = RIBBON_H, mid = H / 2;
    const tx0 = this.tx(tr[0]), tx1 = Math.max(tx0 + 1.5, this.tx(tr[1]));
    const bx0 = this.bx(br[0]), bx1 = Math.max(bx0 + 1.5, this.bx(br[1]));
    r.fillStyle = alpha(col, strong ? 0.5 : 0.4);
    r.strokeStyle = alpha(col, 0.95);
    r.lineWidth = 1;
    r.beginPath();
    r.moveTo(tx0, 0);
    r.bezierCurveTo(tx0, mid, bx0, mid, bx0, H);
    r.lineTo(bx1, H);
    r.bezierCurveTo(bx1, mid, tx1, mid, tx1, 0);
    r.closePath();
    r.fill();
    r.stroke();
  }

  private bandBytes(b: CanvasRenderingContext2D, br: [number, number], seg: Segment | null, col: string, strong: boolean) {
    const x0 = this.bx(br[0]), x1 = this.bx(br[1]);
    const depth = seg ? this.depthOf(seg) : 0;
    const y = seg ? this.rowY(Math.min(depth, ROWS.length - 1)) : 0;
    const hh = seg ? this.rowY(ROWS.length) - y : this.rowY(ROWS.length);
    const w = Math.max(2, x1 - x0);
    b.fillStyle = alpha(col, strong ? 0.22 : 0.18);
    b.fillRect(x0, 0, w, this.rowY(ROWS.length));
    b.strokeStyle = col;
    b.lineWidth = strong ? 2 : 1.5;
    b.strokeRect(x0, y + 1, w, hh - 2);
    if (w < 6) {
      // A tiny block: add a marker so it can't be missed.
      b.fillStyle = col;
      b.beginPath();
      b.moveTo(x0 + w / 2 - 5, 0);
      b.lineTo(x0 + w / 2 + 5, 0);
      b.lineTo(x0 + w / 2, 7);
      b.fill();
    }
  }

  private drawProbe(c: CanvasRenderingContext2D, r: CanvasRenderingContext2D, b: CanvasRenderingContext2D, T: ReturnType<typeof theme>, p: Extract<Target, { kind: 'probe' }>) {
    const t = p.t ?? (p.byte !== null ? this.m.timeAtByte(p.byte) : null);
    const byte = p.byte ?? (p.t !== null ? this.m.byteAtTime(p.t) : null);
    const col = alpha(T.text, 0.85);
    if (t !== null) {
      c.fillStyle = col;
      c.fillRect(Math.round(this.tx(t)), 0, 1, this.axisY);
    }
    if (byte !== null) {
      b.fillStyle = col;
      b.fillRect(Math.round(this.bx(byte)), 0, 1, this.rowY(ROWS.length));
    }
    if (t !== null && byte !== null) {
      const x = this.tx(t), bx = this.bx(byte);
      r.strokeStyle = col;
      r.lineWidth = 1;
      r.setLineDash([3, 3]);
      r.beginPath();
      r.moveTo(x, 0);
      r.bezierCurveTo(x, RIBBON_H / 2, bx, RIBBON_H / 2, bx, RIBBON_H);
      r.stroke();
      r.setLineDash([]);
    }
  }

  private depthCache = new WeakMap<Segment, number>();
  private depthOf(seg: Segment): number {
    let d = this.depthCache.get(seg);
    if (d === undefined) {
      d = Math.max(0, (this.m.pathTo(seg)?.length ?? 1) - 1);
      this.depthCache.set(seg, d);
    }
    return d;
  }

  // ---------------------------------------------------------------- interaction: timeline
  private bindTimeline() {
    const el = this.tlo.el;
    let down: { x: number; t: number; scroll: boolean; v: [number, number] } | null = null;
    const pos = (e: PointerEvent | WheelEvent) => {
      const r = el.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    el.addEventListener('pointerdown', (e) => {
      const { x, y } = pos(e);
      el.setPointerCapture(e.pointerId);
      down = { x, t: this.xt(x), scroll: y >= this.scrollY, v: store.s.view };
      if (down.scroll) this.scrollTo(x);
    });
    el.addEventListener('pointermove', (e) => {
      const { x, y } = pos(e);
      if (down?.scroll) return this.scrollTo(x);
      const t = Math.max(0, Math.min(this.m.duration, this.xt(x)));
      if (down && Math.abs(x - down.x) > 3) {
        const a = Math.max(0, Math.min(down.t, t)), b = Math.min(this.m.duration, Math.max(down.t, t));
        store.set({ sel: { kind: 'time', t0: a, t1: b }, hover: null });
        this.setStatusTime(a, b);
        return;
      }
      if (y > this.axisY) {
        store.set({ hover: null });
        hideTip();
        return;
      }
      store.set({ hover: { kind: 'probe', t, byte: this.m.byteAtTime(t) } });
      this.setStatusProbe(t);
      const cc = store.s.codec;
      const k = Math.floor((y - this.codecY) / LANE_ROW);
      if (cc && y >= this.codecY && k >= 0 && k < cc.lanes.length) {
        const lane = cc.c.lanes[k];
        const fi = this.m.frameIndexAtTime(t);
        const v = fi >= 0 ? cc.lanes[k][fi] : -1;
        const val = v >= 0 ? lane.values[v] : null;
        showTip(`<div class="tt-k">${esc(lane.name)}</div>${val ? `<b>${esc(val.value)}</b><br>${esc(val.help)}` : fi >= cc.decided ? 'Still analysing…' : 'Not applicable here.'}<div class="tt-k" style="margin-top:4px">${esc(lane.help)}</div>`, e.clientX, e.clientY);
      } else hideTip();
    });
    el.addEventListener('pointerup', (e) => {
      const { x } = pos(e);
      const d = down;
      down = null;
      if (!d || d.scroll) return;
      if (Math.abs(x - d.x) > 3) {
        const sel = store.s.sel;
        if (sel?.kind === 'time' && store.s.player) store.s.player.seek(sel.t0);
        return;
      }
      // Click: move the playhead and select the block that plays at this moment.
      const t = Math.max(0, Math.min(this.m.duration, this.xt(x)));
      store.s.player?.seek(t);
      const f = this.frameAtTime(t);
      if (f) store.set({ sel: { kind: 'seg', seg: pickTimedChild(f, t) } });
      this.invalidate('over');
    });
    el.addEventListener('pointerleave', () => {
      hideTip();
      if (!down) store.set({ hover: null });
    });
    el.addEventListener('dblclick', () => {
      const sel = store.s.sel;
      if (sel) playTarget(sel);
    });
    el.addEventListener('wheel', (e) => {
      const { x } = pos(e);
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        zoomTime(Math.exp(e.deltaY * 0.01), this.xt(x));
      } else if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        const [a, b] = store.s.view;
        panTime(((e.shiftKey ? e.deltaY : e.deltaX) / this.width) * (b - a));
      }
    }, { passive: false });
  }

  private scrollTo(x: number) {
    const [a, b] = store.s.view;
    const w = b - a;
    const D = this.m.duration;
    const c = (x / this.width) * D;
    const na = Math.max(0, Math.min(D - w, c - w / 2));
    store.set({ view: [na, na + w] });
  }

  // ---------------------------------------------------------------- interaction: bytes
  /** The segment path under x at row `depth`, choosing segments at least 2 px wide. */
  private hitBytes(x: number, y: number): Segment | null {
    const byte = this.xb(x);
    let depthWanted = 0;
    for (let d = 0, yy = 0; d < ROWS.length; d++) {
      yy += ROWS[d];
      if (y < yy) { depthWanted = d; break; }
      depthWanted = d;
    }
    let list = this.m.top;
    let found: Segment | null = null;
    for (let d = 0; d <= depthWanted; d++) {
      const i = firstEndingAfter(list, byte);
      const s = list[i];
      if (!s || s.start > byte) break;
      if (d > 0 && this.bx(s.end) - this.bx(s.start) < 2) break;
      found = s;
      if (!hasKids(s)) break;
      const lazy = !Array.isArray(s.children);
      if (lazy && this.bx(s.end) - this.bx(s.start) <= 60) break;
      list = kids(s);
    }
    return found;
  }

  private bindBytes() {
    const el = this.bmo.el;
    let drag: { x: number; bview: [number, number]; moved: boolean } | null = null;
    const pos = (e: PointerEvent | WheelEvent | MouseEvent) => {
      const r = el.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    el.addEventListener('pointerdown', (e) => {
      el.setPointerCapture(e.pointerId);
      drag = { x: pos(e).x, bview: store.s.bview, moved: false };
    });
    el.addEventListener('pointermove', (e) => {
      const { x, y } = pos(e);
      if (drag && Math.abs(x - drag.x) > 3) {
        drag.moved = true;
        const [a, b] = drag.bview;
        const du = ((drag.x - x) / this.width) * (b - a);
        const na = Math.max(0, Math.min(1 - (b - a), a + du));
        store.set({ bview: [na, na + (b - a)] });
        hideTip();
        return;
      }
      if (y > this.rowY(ROWS.length)) {
        store.set({ hover: { kind: 'probe', byte: this.xb(x), t: null } });
        hideTip();
        return;
      }
      const seg = this.hitBytes(x, y);
      if (seg) {
        store.set({ hover: { kind: 'seg', seg } });
        showTip(this.segTip(seg, this.xb(x)), e.clientX, e.clientY);
        this.setStatusSeg(seg, this.xb(x));
      }
    });
    el.addEventListener('pointerup', (e) => {
      const d = drag;
      drag = null;
      if (!d || d.moved) return;
      const { x, y } = pos(e);
      const seg = this.hitBytes(x, y);
      if (seg) {
        store.set({ sel: { kind: 'seg', seg } });
        const tr = timeOf(this.m, seg);
        if (tr && store.s.player) store.s.player.seek(tr[0]);
      }
    });
    el.addEventListener('pointerleave', () => {
      hideTip();
      if (!drag) store.set({ hover: null });
    });
    el.addEventListener('dblclick', () => store.set({ bview: [0, 1] }));
    el.addEventListener('wheel', (e) => {
      const { x } = pos(e);
      const [a, b] = store.s.bview;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        zoomBytes(Math.exp(e.deltaY * 0.01), a + (x / this.width) * (b - a));
      } else if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        const du = ((e.shiftKey ? e.deltaY : e.deltaX) / this.width) * (b - a);
        const na = Math.max(0, Math.min(1 - (b - a), a + du));
        store.set({ bview: [na, na + (b - a)] });
      }
    }, { passive: false });
    tip(this.rb.el, () => '<b>Ribbons</b><br>Each band joins a block of bytes (bottom) to the stretch of sound it makes (top). Wide at the bottom and thin at the top means many bytes for a short time: a high bitrate.');
  }

  private segTip(s: Segment, byte: number): string {
    const tr = timeOf(this.m, s);
    const inflated = this.m.layout.inflated.has(s) ? '<div class="tt-k">Drawn larger than true size so you can see it.</div>' : '';
    return `<div class="tt-k">${KIND_LABEL[s.kind]}</div><b>${esc(s.name)}</b>${s.summary ? `<div>${esc(s.summary)}</div>` : ''}
      <div class="mono">${fmtOff(s.start)}–${fmtOff(s.end)} · ${fmtBytes(s.end - s.start)}</div>
      ${tr ? `<div class="mono">plays ${fmtTime(tr[0], tr[1] - tr[0])} → ${fmtTime(tr[1], tr[1] - tr[0])}</div>` : '<div class="tt-k">Not audio: this block makes no sound.</div>'}
      ${inflated}<div class="tt-k mono">byte ${Math.floor(byte).toLocaleString('en-US')}</div>`;
  }

  // ---------------------------------------------------------------- status line
  private setStatus(...parts: (string | Node)[]) {
    this.status.replaceChildren(...parts.map((p) => (typeof p === 'string' ? h('span', {}, p) : p)));
  }
  private setStatusProbe(t: number) {
    const f = this.frameAtTime(t);
    const byte = this.m.byteAtTime(t);
    const parts: (string | Node)[] = [`time ${fmtTime(t, 0.5)}`];
    if (byte !== null) parts.push(`byte ${Math.floor(byte).toLocaleString('en-US')} (${fmtOff(byte)})`);
    if (f) {
      const idx = this.m.frames.indexOf(f);
      const sw = h('span', {}, h('span', { class: 'sw', style: { background: theme().kind.audio } }), `${f.name}${this.m.frames.length > 1 ? ` (${idx + 1} of ${this.m.frames.length.toLocaleString('en-US')})` : ''}`);
      parts.push(sw, f.summary ?? '');
    }
    this.setStatus(...parts);
  }
  private setStatusSeg(s: Segment, byte: number) {
    const tr = timeOf(this.m, s);
    this.setStatus(
      h('span', {}, h('span', { class: 'sw', style: { background: theme().kind[s.kind] } }), s.name),
      `byte ${Math.floor(byte).toLocaleString('en-US')} (${fmtOff(byte)})`,
      fmtBytes(s.end - s.start),
      tr ? `${fmtTime(tr[0], tr[1] - tr[0])} → ${fmtTime(tr[1], tr[1] - tr[0])}` : 'no playback time',
    );
  }
  private setStatusTime(a: number, b: number) {
    const br = this.m.bytesForTime(a, b);
    const parts = [`selection ${fmtTime(a, b - a)} → ${fmtTime(b, b - a)} (${((b - a) * 1000).toFixed(0)} ms)`];
    if (br) {
      const n = br[1] - br[0];
      parts.push(`${fmtBytes(Math.round(n))} of audio data`, `≈ ${Math.round((n * 8) / Math.max(1e-6, b - a) / 1000)} kbps`);
      const i0 = this.m.frameIndexAtTime(a), i1 = this.m.frameIndexAtTime(b);
      if (this.m.frames.length > 1 && i0 >= 0) parts.push(plural(i1 - i0 + 1, 'block'));
    }
    this.setStatus(...parts);
  }
}

const esc = (s: string) => richText(s);

const linkCache = new WeakMap<Segment, SegmentLink[]>();
export function linksOf(s: Segment): SegmentLink[] {
  if (!s.links) return [];
  let l = linkCache.get(s);
  if (!l) {
    try { l = s.links(); } catch (e) { console.warn('links() failed', e); l = []; }
    linkCache.set(s, l);
  }
  return l;
}

function firstEndingAfter(list: Segment[], byte: number): number {
  let lo = 0, hi = list.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (list[mid].end <= byte) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** For big frames with timed children (a WAV data chunk split into seconds), pick the child at t. */
function pickTimedChild(f: Segment, t: number): Segment {
  if (!hasKids(f)) return f;
  const ch = kids(f);
  const c = ch.find((x) => x.time && x.time[0] <= t && t < x.time[1]);
  return c ?? f;
}

function hatch(c: CanvasRenderingContext2D, x: number, y: number, w: number, hh: number, col: string) {
  c.save();
  c.beginPath();
  c.rect(x, y, w, hh);
  c.clip();
  c.strokeStyle = alpha(col.startsWith('#') ? col : '#000000', 0.35);
  c.lineWidth = 1;
  for (let i = -hh; i < w; i += 5) {
    c.beginPath();
    c.moveTo(x + i, y + hh);
    c.lineTo(x + i + hh, y);
    c.stroke();
  }
  c.restore();
}

// ------------------------------------------------------------------ view helpers
function clampView(a: number, b: number): [number, number] {
  const D = store.s.model?.duration || 1;
  const minW = Math.min(D, 0.002);
  let w = Math.max(minW, Math.min(D, b - a));
  a = Math.max(0, Math.min(D - w, a));
  return [a, a + w];
}
export function zoomTime(factor: number, anchor?: number) {
  const [a, b] = store.s.view;
  const c = anchor ?? (store.s.player?.playing ? store.s.player.time : (a + b) / 2);
  const f = (c - a) / (b - a);
  const w = (b - a) * factor;
  store.set({ view: clampView(c - f * w, c - f * w + w) });
}
export function panTime(dt: number) {
  const [a, b] = store.s.view;
  store.set({ view: clampView(a + dt, b + dt) });
}
export function zoomBytes(factor: number, anchor: number) {
  const [a, b] = store.s.bview;
  const size = store.s.model?.pf.size || 1;
  const minW = Math.min(1, 24 / size);
  const f = (anchor - a) / (b - a);
  const w = Math.max(minW, Math.min(1, (b - a) * factor));
  let na = anchor - f * w;
  na = Math.max(0, Math.min(1 - w, na));
  store.set({ bview: [na, na + w] });
}
export function fitAll() {
  const D = store.s.model?.duration || 1;
  store.set({ view: [0, D], bview: [0, 1] });
}
export function zoomToSelection() {
  const s = store.s, m = s.model;
  if (!m || !s.sel) return;
  let tr: [number, number] | null = null, br: [number, number] | null = null;
  if (s.sel.kind === 'seg') {
    tr = timeOf(m, s.sel.seg);
    br = [s.sel.seg.start, s.sel.seg.end];
  } else if (s.sel.kind === 'time') {
    tr = [s.sel.t0, s.sel.t1];
    br = m.bytesForTime(s.sel.t0, s.sel.t1);
  }
  const patch: Partial<State> = {};
  if (tr) {
    const pad = Math.max((tr[1] - tr[0]) * 0.15, 0.002);
    patch.view = clampView(tr[0] - pad, tr[1] + pad);
  }
  if (br) {
    const u0 = m.layout.toU(br[0]), u1 = m.layout.toU(br[1]);
    const pad = Math.max((u1 - u0) * 0.15, 8 / m.pf.size);
    const a = Math.max(0, u0 - pad), b = Math.min(1, u1 + pad);
    patch.bview = [a, b];
  }
  store.set(patch);
}
/** Keeps a newly selected block visible without changing the zoom. */
export function reveal(t: Target) {
  const m = store.s.model;
  if (!m) return;
  if (t.kind === 'seg') {
    const tr = timeOf(m, t.seg);
    const [a, b] = store.s.view;
    if (tr && (tr[1] < a || tr[0] > b)) {
      const w = Math.max(b - a, tr[1] - tr[0]);
      const c = (tr[0] + tr[1]) / 2;
      store.set({ view: clampView(c - w / 2, c + w / 2) });
    }
    const [u0, u1] = store.s.bview;
    const s0 = m.layout.toU(t.seg.start), s1 = m.layout.toU(t.seg.end);
    if (s1 < u0 || s0 > u1) {
      const w = u1 - u0;
      const c = (s0 + s1) / 2;
      const na = Math.max(0, Math.min(1 - w, c - w / 2));
      store.set({ bview: [na, na + w] });
    }
  }
}

export function togglePlay() {
  const s = store.s;
  const p = s.player;
  if (!p) return;
  if (p.playing) return p.pause();
  const sel = s.sel;
  if (sel && sel.kind !== 'probe') {
    const tr = sel.kind === 'seg' ? (s.model ? timeOf(s.model, sel.seg) : null) : ([sel.t0, sel.t1] as [number, number]);
    if (tr && tr[1] > tr[0]) {
      const start = p.time >= tr[0] && p.time < tr[1] - 0.01 ? p.time : tr[0];
      return p.play(start, tr[1], s.loop);
    }
  }
  const start = p.time >= (s.model?.duration ?? 0) - 0.01 ? 0 : p.time;
  p.play(start);
}

export function playTarget(t: Target) {
  const s = store.s;
  const p = s.player, m = s.model;
  if (!p || !m) return;
  const tr = t.kind === 'seg' ? timeOf(m, t.seg) : t.kind === 'time' ? ([t.t0, t.t1] as [number, number]) : null;
  if (tr) p.play(tr[0], tr[1], s.loop);
}

// ------------------------------------------------------------------ spectrogram
class Spectro {
  private worker = new SpectroWorker();
  private img: HTMLCanvasElement | null = null;
  private imgView: [number, number] = [0, 0];
  private want: string = '';
  private id = 0;
  private timer = 0;
  readonly maxF: number;
  constructor(peaks: import('../audio/peaks').Peaks, private onReady: () => void) {
    const n = peaks.channels[0].length;
    const mono = new Float32Array(n);
    for (const ch of peaks.channels) for (let i = 0; i < n; i++) mono[i] += ch[i] / peaks.channels.length;
    this.maxF = peaks.sampleRate / 2;
    this.worker.postMessage({ type: 'data', samples: mono, rate: peaks.sampleRate }, [mono.buffer]);
    this.worker.onmessage = (e: MessageEvent<{ id: number; width: number; height: number; px: Uint8ClampedArray; view: [number, number] }>) => {
      const { width, height, px, id } = e.data;
      if (id !== this.id) return;
      const cv = document.createElement('canvas');
      cv.width = width;
      cv.height = height;
      cv.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(px), width, height), 0, 0);
      this.img = cv;
      this.imgView = this.pendingView;
      this.onReady();
    };
  }
  private pendingView: [number, number] = [0, 0];
  draw(c: CanvasRenderingContext2D, x: number, y: number, w: number, hh: number, view: [number, number]) {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const pw = Math.round(w * dpr), ph = Math.round(hh * dpr);
    const key = `${view[0]}:${view[1]}:${pw}:${ph}`;
    if (this.img) {
      // Draw the last image mapped to the current view while a new one renders.
      const [a, b] = this.imgView;
      const sx = ((view[0] - a) / (b - a)) * this.img.width;
      const sw = ((view[1] - view[0]) / (b - a)) * this.img.width;
      c.save();
      c.imageSmoothingEnabled = true;
      c.fillStyle = '#000';
      c.fillRect(x, y, w, hh);
      c.drawImage(this.img, sx, 0, sw, this.img.height, x, y, w, hh);
      c.restore();
    } else {
      c.fillStyle = '#000';
      c.fillRect(x, y, w, hh);
    }
    if (key !== this.want) {
      this.want = key;
      clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        this.id++;
        this.pendingView = view;
        const span = view[1] - view[0];
        const spp = (span * this.maxF * 2) / pw;
        // Longer FFTs resolve pitch better; shorter ones resolve time better when zoomed in.
        const fftSize = spp < 64 ? 512 : spp < 256 ? 1024 : 2048;
        this.worker.postMessage({ type: 'render', id: this.id, t0: view[0], t1: view[1], width: Math.min(pw, 2400), height: ph, fftSize, maxFreq: this.maxF });
      }, 60);
    }
  }
  dispose() {
    this.worker.terminate();
  }
}
